import React, { useCallback, useEffect, useRef, useState } from "react";
import { Briefcase, Building2, ChevronDown, Layers, RefreshCw, Search } from "lucide-react";
import MarketingTable from "@/components/marketing/MarketingTable";
import MarketingComposeModal from "@/components/marketing/MarketingComposeModal";
import ApplicationFilters from "@/components/Companies/ApplicationFilters";
import Pagination from "@/components/Common/Pagination";
import Dropdown from "@/components/ui/Dropdown";
import { newsletterService } from "@/services/newsletterService";
import { companiesService } from "@/services/companiesService";
import { campaignsService } from "@/services/campaignsService";
import { vacancyService } from "@/services/vacancyService";
import usePaginationStore from "@/hooks/stores/usePaginationStore";
import { useDebounce } from "@/utils/helpers";
import useToastStore from "@/components/Common/Toast/toastStore";

const CANDIDATES_TABLE_KEY = "marketing-candidates-table";
const VISIBLE_COLUMNS_STORAGE_KEY = "marketing_candidates_visibleColumns";
const DEFAULT_PER_PAGE = 50;

const columns = [
  { key: "name", label: "Name", filterable: true, inputType: "text" },
  { key: "email", label: "Email", filterable: true, inputType: "text" },
  {
    key: "phone_number",
    label: "Phone Number",
    filterable: true,
    inputType: "text",
  },
  {
    key: "gender",
    label: "Gender",
    filterable: true,
    inputType: "text",
  },
  {
    key: "sms_opt_in",
    label: "Opt SMS",
    filterable: true,
    inputType: "select",
    options: [
      { label: "Yes", value: "1" },
      { label: "No", value: "0" },
    ],
  },
  {
    key: "email_opt_in",
    label: "Opt Email",
    filterable: true,
    inputType: "select",
    options: [
      { label: "Yes", value: "1" },
      { label: "No", value: "0" },
    ],
  },
];

const defaultVisibleColumns = columns.reduce((acc, column) => {
  acc[column.key] = true;
  return acc;
}, {});

const getInitialVisibleColumns = () => {
  try {
    const saved = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (!saved) return { ...defaultVisibleColumns };
    const parsed = JSON.parse(saved);
    return columns.reduce((acc, column) => {
      acc[column.key] =
        typeof parsed[column.key] === "boolean"
          ? parsed[column.key]
          : true;
      return acc;
    }, {});
  } catch {
    return { ...defaultVisibleColumns };
  }
};

const normalizeList = (response, keys = []) => {
  if (Array.isArray(response)) return response;
  for (const key of keys) {
    if (Array.isArray(response?.[key])) return response[key];
    if (Array.isArray(response?.data?.[key])) return response.data[key];
  }
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getFilterValue = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined;
    return value[0];
  }
  return value;
};

const buildApiParams = (
  searchQuery,
  filters,
  activeFilters,
  scopeFilters,
  { page, per_page },
) => {
  const params = {
    any_opt_in: 1,
    page,
    per_page,
  };

  if (searchQuery.trim()) {
    params.search = searchQuery.trim();
  }

  if (scopeFilters.company_id) {
    params.company_id = scopeFilters.company_id;
  }
  if (scopeFilters.campaign_id) {
    params.campaign_id = scopeFilters.campaign_id;
  }
  if (scopeFilters.vacancy_id) {
    params.vacancy_id = scopeFilters.vacancy_id;
  }

  activeFilters.forEach((filterKey) => {
    const filterValue = getFilterValue(filters[filterKey]);
    if (filterValue === undefined) return;

    if (filterKey === "sms_opt_in" || filterKey === "email_opt_in") {
      params[filterKey] = filterValue;
      return;
    }

    if (["name", "email", "phone_number", "gender"].includes(filterKey)) {
      params[filterKey] = filterValue;
    }
  });

  return params;
};

const MarketingCandidates = () => {
  const { getPerPage, setPerPage } = usePaginationStore();
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("marketing_filters");
    return saved ? JSON.parse(saved) : {};
  });
  const [activeFilters, setActiveFilters] = useState(() => {
    const saved = localStorage.getItem("marketing_activeFilters");
    return saved ? JSON.parse(saved) : [];
  });
  const [filterInputs, setFilterInputs] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const columnToggleRef = useRef(null);
  const [composeModal, setComposeModal] = useState({
    isOpen: false,
    type: "email",
  });
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: getPerPage(CANDIDATES_TABLE_KEY) || DEFAULT_PER_PAGE,
    total: 0,
    last_page: 1,
  });

  const [companies, setCompanies] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingVacancies, setIsLoadingVacancies] = useState(false);

  const { showToast } = useToastStore;

  useEffect(() => {
    localStorage.setItem(
      VISIBLE_COLUMNS_STORAGE_KEY,
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnToggleRef.current &&
        !columnToggleRef.current.contains(event.target)
      ) {
        setShowColumnToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const resetListState = () => {
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
    setSelectedContacts([]);
  };

  const openComposeModal = (type) => {
    if (selectedContacts.length === 0) {
      showToast({
        message: "Please select at least one contact",
        type: "error",
      });
      return;
    }
    setComposeModal({ isOpen: true, type });
  };

  const closeComposeModal = () => {
    setComposeModal((prev) => ({ ...prev, isOpen: false }));
  };

  const updateSearchTerm = (term) => {
    setSearchQuery(term);
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  const handlePageChange = (page) => {
    setServerPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
    setSelectedContacts([]);
  };

  const handlePerPageChange = (selectedOption) => {
    const newPerPage = selectedOption.value;
    setPerPage(CANDIDATES_TABLE_KEY, newPerPage);
    setServerPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1,
    }));
    setSelectedContacts([]);
  };

  const handleCompanyChange = (selected) => {
    setSelectedCompany(selected || null);
    setSelectedCampaign(null);
    setSelectedVacancy(null);
    setCampaigns([]);
    setVacancies([]);
    resetListState();
  };

  const handleCampaignChange = (selected) => {
    setSelectedCampaign(selected || null);
    setSelectedVacancy(null);
    setVacancies([]);
    resetListState();
  };

  const handleVacancyChange = (selected) => {
    setSelectedVacancy(selected || null);
    resetListState();
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        const response = await companiesService.getCompaniesList({
          params: {
            page: 1,
            per_page: 100,
          },
        });
        setCompanies(normalizeList(response, ["companies", "data"]));
      } catch (err) {
        console.error("Error fetching companies:", err);
        setCompanies([]);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!selectedCompany?.value) {
        setCampaigns([]);
        return;
      }

      try {
        setIsLoadingCampaigns(true);
        const response = await campaignsService.getCampaignByCompanyId(
          selectedCompany.value,
        );
        const list = normalizeList(response, ["campaigns"]);
        setCampaigns(Array.isArray(list) ? list : list?.campaigns || []);
      } catch (err) {
        console.error("Error fetching campaigns:", err);
        setCampaigns([]);
      } finally {
        setIsLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, [selectedCompany?.value]);

  useEffect(() => {
    const fetchVacancies = async () => {
      if (!selectedCompany?.value || !selectedCampaign?.value) {
        setVacancies([]);
        return;
      }

      try {
        setIsLoadingVacancies(true);
        const response = await vacancyService.getVacancies(
          selectedCompany.value,
          {
            campaign_id: selectedCampaign.value,
            status: [0, 1],
          },
        );
        setVacancies(normalizeList(response, ["vacancies"]));
      } catch (err) {
        console.error("Error fetching vacancies:", err);
        setVacancies([]);
      } finally {
        setIsLoadingVacancies(false);
      }
    };

    fetchVacancies();
  }, [selectedCompany?.value, selectedCampaign?.value]);

  const fetchContacts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await newsletterService.getSubscribers(
        buildApiParams(
          searchQuery,
          filters,
          activeFilters,
          {
            company_id: selectedCompany?.value,
            campaign_id: selectedCampaign?.value,
            vacancy_id: selectedVacancy?.value,
          },
          {
            page: serverPagination.current_page,
            per_page: serverPagination.per_page || DEFAULT_PER_PAGE,
          },
        ),
      );

      const rows = (res?.data || []).map((item) => ({
        id: item.id,
        user_id: item.user_id ?? item.user?.id,
        name: item.user?.name || "-",
        email: item.user?.email || "-",
        phone_number: item.user?.phone_number || "-",
        gender: item.user?.gender || "-",
        email_opt_in: item.email_opt_in,
        sms_opt_in: item.sms_opt_in,
      }));

      setContacts(rows);

      if (res?.pagination) {
        setServerPagination((prev) => ({
          ...prev,
          current_page:
            res.pagination.current_page ?? prev.current_page,
          total: res.pagination.total ?? prev.total,
          last_page: res.pagination.last_page ?? prev.last_page,
          // Keep client-selected per_page; API often echoes default (50)
        }));
      }
    } catch (err) {
      setError(err.message || "Failed to load marketing contacts");
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    searchQuery,
    filters,
    activeFilters,
    selectedCompany?.value,
    selectedCampaign?.value,
    selectedVacancy?.value,
    serverPagination.current_page,
    serverPagination.per_page,
  ]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const companyOptions = companies.map((company) => ({
    label: company.name,
    value: company.id,
  }));

  const campaignOptions = campaigns.map((campaign) => ({
    label: campaign.title || campaign.name || `Campaign #${campaign.id}`,
    value: campaign.id,
  }));

  const vacancyOptions = vacancies.map((vacancy) => ({
    label: vacancy.title || vacancy.name || `Vacancy #${vacancy.id}`,
    value: vacancy.id,
  }));

  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      {error && (
        <div className="p-3 sm:p-4 bg-red-100 text-red-700 rounded-md border border-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-wrap justify-start gap-4 bg-card p-4 rounded-lg border border-border">
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-foreground mb-1">
            Company
          </label>
          <div className="flex items-center gap-2">
            <Dropdown
              options={companyOptions}
              value={selectedCompany}
              onChange={handleCompanyChange}
              placeholder={
                isLoadingCompanies ? "Loading companies..." : "Select Company"
              }
              icon={
                <Building2 className="w-5 h-5 text-foreground flex-shrink-0" />
              }
              searchable={true}
              className="min-h-[50px] w-auto min-w-[220px] border border-border rounded-lg"
              dropdownWidth="w-auto min-w-[30%]"
              disabled={isLoadingCompanies}
            />
            {selectedCompany && (
              <button
                type="button"
                className="p-1 rounded hover:bg-muted focus:outline-none"
                onClick={() => handleCompanyChange(null)}
                title="Clear company filter"
              >
                <RefreshCw className="w-5 h-5 text-foreground" />
              </button>
            )}
          </div>
        </div>

        {selectedCompany && (
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-foreground mb-1">
              Campaign
            </label>
            <div className="flex items-center gap-2">
              <Dropdown
                options={campaignOptions}
                value={selectedCampaign}
                onChange={handleCampaignChange}
                placeholder={
                  isLoadingCampaigns
                    ? "Loading campaigns..."
                    : campaigns.length > 0
                      ? "Select Campaign"
                      : "No Campaigns"
                }
                icon={
                  <Layers className="w-5 h-5 text-foreground flex-shrink-0" />
                }
                searchable={true}
                className="min-h-[50px] w-auto min-w-[220px] border border-border rounded-lg"
                dropdownWidth="w-auto min-w-[30%]"
                disabled={isLoadingCampaigns || campaigns.length === 0}
              />
              {selectedCampaign && (
                <button
                  type="button"
                  className="p-1 rounded hover:bg-muted focus:outline-none"
                  onClick={() => handleCampaignChange(null)}
                  title="Clear campaign filter"
                >
                  <RefreshCw className="w-5 h-5 text-foreground" />
                </button>
              )}
            </div>
          </div>
        )}

        {selectedCampaign && (
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-foreground mb-1">
              Vacancy
            </label>
            <div className="flex items-center gap-2">
              <Dropdown
                options={vacancyOptions}
                value={selectedVacancy}
                onChange={handleVacancyChange}
                placeholder={
                  isLoadingVacancies
                    ? "Loading vacancies..."
                    : vacancies.length > 0
                      ? "Select Vacancy"
                      : "No Vacancies"
                }
                icon={
                  <Briefcase className="w-5 h-5 text-foreground flex-shrink-0" />
                }
                searchable={true}
                className="min-h-[50px] w-auto min-w-[220px] border border-border rounded-lg"
                dropdownWidth="w-auto min-w-[30%]"
                disabled={isLoadingVacancies || vacancies.length === 0}
              />
              {selectedVacancy && (
                <button
                  type="button"
                  className="p-1 rounded hover:bg-muted focus:outline-none"
                  onClick={() => handleVacancyChange(null)}
                  title="Clear vacancy filter"
                >
                  <RefreshCw className="w-5 h-5 text-foreground" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="relative z-10 flex w-full flex-wrap items-center gap-3">
        <div className="w-full sm:max-w-[384px] shrink-0">
          <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              id="marketing-candidates-search"
              type="text"
              placeholder="Search"
              value={tempSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
            />
          </div>
        </div>

        <div className="ml-auto flex w-full sm:w-auto shrink-0 items-center justify-start sm:justify-end gap-2">
          <ApplicationFilters
            columns={columns}
            visibleColumns={visibleColumns}
            processedApplications={contacts}
            onFiltersChange={(nextFilters, nextActiveFilters) => {
              setFilters(nextFilters);
              setActiveFilters(nextActiveFilters);
              setServerPagination((prev) => ({ ...prev, current_page: 1 }));
              setSelectedContacts([]);
            }}
            filtersStorageKey="marketing_filters"
            activeFiltersStorageKey="marketing_activeFilters"
            filterInputTriggerClasses="bg-background"
            filterInputsContainerClasses="w-full flex-wrap justify-start md:justify-end"
            getFiltersComponent={(_filterInputs) =>
              setFilterInputs(_filterInputs)
            }
          />

          <div className="relative" ref={columnToggleRef}>
            <button
              type="button"
              className="w-40 h-[36px] px-3 py-2 bg-primary-dark rounded-lg shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] outline outline-2 outline-offset-[-2px] outline-white/10 flex justify-center items-center gap-1 overflow-hidden group hover:bg-primary-dark transition-all"
              onClick={() => setShowColumnToggle(!showColumnToggle)}
            >
              <span className="text-white text-sm font-semibold font-['Inter'] leading-5">
                Columns
              </span>
              <ChevronDown className="h-4 w-4 text-white" />
            </button>

            {showColumnToggle && (
              <div className="absolute right-0 top-12 z-50 mt-2 bg-card shadow-lg rounded-md border p-3 min-w-[200px] max-w-[300px]">
                <div className="font-medium pb-2 border-b mb-2">
                  Column Options
                </div>
                {columns.map((column) => {
                  const isChecked = !!visibleColumns[column.key];

                  return (
                    <div
                      key={column.key}
                      className="flex items-center gap-3 py-2 px-3 hover:bg-muted rounded-md cursor-pointer transition-colors"
                      onClick={() => toggleColumnVisibility(column.key)}
                    >
                      <div
                        className={`w-4 h-4 relative rounded border flex items-center justify-center shrink-0 transition-all ${
                          isChecked
                            ? "bg-secondary border-secondary"
                            : "bg-card border-input"
                        }`}
                      >
                        {isChecked && (
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span
                        className={`text-sm ${isChecked ? "font-semibold text-foreground" : "text-muted-foreground"}`}
                      >
                        {column.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => openComposeModal("sms")}
              className="bg-primary-dark text-white px-4 py-2 rounded-md"
            >
              Send SMS
            </button>
            <button
              type="button"
              onClick={() => openComposeModal("email")}
              className="bg-primary-dark text-white px-4 py-2 rounded-md"
            >
              Send Email
            </button>
          </div>
        </div>
      </div>

      {filterInputs && filterInputs != null && activeFilters.length > 0 && (
        <div className="relative z-10 w-full pb-1 flex flex-wrap justify-end">
          {filterInputs}
        </div>
      )}

      <MarketingTable
        contacts={contacts}
        isLoading={isLoading}
        searchTerm={searchQuery}
        selectedContacts={selectedContacts}
        setSelectedContacts={setSelectedContacts}
        visibleColumns={visibleColumns}
      />

      {!isLoading && (
        <Pagination
          currentPage={serverPagination.current_page}
          totalPages={serverPagination.last_page}
          per_page={serverPagination.per_page}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />
      )}

      <MarketingComposeModal
        isOpen={composeModal.isOpen}
        type={composeModal.type}
        onClose={closeComposeModal}
        selectedContacts={contacts.filter((contact) =>
          selectedContacts.includes(contact.id),
        )}
        onSuccess={() => setSelectedContacts([])}
      />
    </div>
  );
};

export default MarketingCandidates;
